import { convertDateRangeToDates } from "@wingspanhq/fe-component-library";
import { paymentsApiSpec } from "@wingspanhq/wingspan-api-types";
import { UrlQuerySort } from "../../../../../shared/hooks/useUrlQuery";
import { getApiDirectionFromTable } from "../../../../../shared/utils/sorting";
import { Filter, PayablesTabFilter, SortField } from "./types";

type ListPayablesRequest = NonNullable<
  paymentsApiSpec.operations["listPayables"]["parameters"]["query"]
>;

export function getTabFilter(
  tab: PayablesTabFilter,
  isDualStage = false
): ListPayablesRequest {
  switch (tab) {
    case "draft":
      return { "filter[status][in]": ["Draft"], "sort[updatedAt]": "desc" };
    case "to-approve":
      if (isDualStage) {
        return {
          excludeDeclined: "true",
          "filter[status][in]": ["Pending", "Open", "Overdue"],
          "filter[payer.workflowStatus][in]": ["PreApproved"],
          "sort[internal.events.paymentDueAt]": "asc"
        };
      }

      return {
        excludeDeclined: "true",
        "filter[status][in]": ["Pending", "Open", "Overdue"],
        "filter[payer.workflowStatus][notIn]": [
          "Approved",
          "PaymentInitiated",
          "Funded"
        ],
        "sort[internal.events.paymentDueAt]": "asc"
      };
    case "to-preapprove":
      return {
        "filter[status][in]": ["Pending", "Open", "Overdue"],
        excludeDeclined: "true",
        "filter[payer.workflowStatus][notIn]": [
          "PreApproved",
          "Approved",
          "PaymentInitiated",
          "Funded"
        ],
        "sort[internal.events.paymentDueAt]": "asc"
      };
    case "scheduled":
      return {
        "filter[status][in]": ["Pending", "Open", "Overdue"],
        "filter[payer.workflowStatus][in]": ["Approved", "PaymentInitiated"],
        "sort[internal.events.paymentDueAt]": "asc"
      };
    case "paid":
      return {
        "filter[status][in]": ["Paid", "PaymentInTransit"],
        "sort[events.paidAt]": "desc"
      };
    default:
      // Default to all
      return {
        "filter[status][!=]": "Cancelled",
        "sort[updatedAt]": "desc"
      };
  }
}

type RequestOptions = {
  isDualStage?: boolean;
  materializedPagination?: boolean;
  payrollRunId?: string;
};

export function createListRequest(
  _userId: string,
  filter: Filter | undefined,
  sort: UrlQuerySort<SortField> | undefined,
  tab: PayablesTabFilter,
  options?: RequestOptions
): ListPayablesRequest {
  let request: ListPayablesRequest = {};

  // Status from tab.
  const tabFilter = getTabFilter(tab, options?.isDualStage);
  request = tabFilter;

  if (options?.materializedPagination) {
    request["page[mode]"] = "materialized";
  }

  if (options?.payrollRunId) {
    request["filter[internal.payroll.payrollId][=]"] = options.payrollRunId;
  }

  if (filter?.payeeId) {
    request["filter[payeeId][=]"] = filter.payeeId;
  }

  if (filter?.bulkBatchId) {
    request["filter[lineItems.labels.bulkBatchId][=]"] = filter.bulkBatchId;
  }

  if (filter?.dueDateRange) {
    const range = convertDateRangeToDates(filter.dueDateRange);
    if (range?.start) {
      request["filter[dueDate][>=]"] = range.start;
    }
    if (range?.end) {
      request["filter[dueDate][<=]"] = range.end;
    }
  }

  if (filter?.paidDateRange) {
    const range = convertDateRangeToDates(filter.paidDateRange);
    if (range?.start) {
      request["filter[events.paidAt][>]"] = range.start;
    }
    if (range?.end) {
      request["filter[events.paidAt][<]"] = range.end;
    }
  }

  if (filter?.openedDateRange) {
    const range = convertDateRangeToDates(filter.openedDateRange);
    if (range?.start) {
      request["filter[events.openedAt][>]"] = range.start;
    }
    if (range?.end) {
      request["filter[events.openedAt][<]"] = range.end;
    }
  }

  // User selected ort.
  const direction = getApiDirectionFromTable(sort?.direction);
  if (sort && direction) {
    if (sort.field === "createdAt") {
      request["sort[createdAt]"] = direction;
    } else if (sort.field === "updatedAt") {
      request["sort[updatedAt]"] = direction;
    } else if (sort.field === "events.paidAt") {
      request["sort[events.paidAt]"] = direction;
    }
  }

  return request;
}
